'use client';

import type { StreamPlayerApi } from '@cloudflare/stream-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

const Stream = dynamic(() => import('@cloudflare/stream-react').then(m => m.Stream), {
  ssr: false,
});

interface VideoExplainerProps {
  title?: string;
  subtitle?: string;
  videoId?: string;
  customerCode?: string;
  posterUrl?: string;
  background?: string;
  titleColor?: string;
  containerWidth?: string;
  fallbackVideoUrl?: string;
  subtitleColorClass?: string;
  ctaClassName?: string;
}

const VideoExplainer: React.FC<VideoExplainerProps> = ({
  title = 'See Right2Hope in Action',
  subtitle = 'Watch our explainer video to understand how we connect patients with life-changing experimental treatments.',
  videoId = '91c618f4e936c4830a07749a968bf04b',
  customerCode = 'c1fzbkeuu2q1nb8a',
  posterUrl = '/images/video-thumb.jpg',
  background = 'bg-white',
  titleColor = 'text-primary',
  containerWidth = 'max-w-4xl',
  fallbackVideoUrl,
  subtitleColorClass = 'text-default-700',
  ctaClassName = 'rh-btn-black',
}) => {
  const [showOverlay, setShowOverlay] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(videoId);
  const [currentCustomerCode, setCurrentCustomerCode] = useState(customerCode);

  // Demo video fallback (Cloudflare's public demo)
  const demoVideoId = '5d5bc37ffcf54c9b82e996823bffbb81';
  const demoCustomerCode = 'f256e09f882b';

  const streamRef = useRef<StreamPlayerApi>(undefined);

  const handlePlayClick = () => {
    setShowOverlay(false);
  };

  useEffect(() => {
    if (showOverlay) return;
    if (!streamRef.current) return;

    // Attempt to start playback immediately so the user doesn’t have to click twice
    streamRef.current.play().catch(() => {
      // Some browsers block autoplay with sound; retry muting, then play.
      try {
        (streamRef.current as StreamPlayerApi).muted = true;
        (streamRef.current as StreamPlayerApi).play();
      } catch (err) {
        /* ignore */
      }
    });
  }, [showOverlay]);

  const handleVideoError = (event: Event) => {
    console.error('Video failed to load:', event);

    // Try demo video if main video fails
    if (currentVideoId === videoId) {
      setCurrentVideoId(demoVideoId);
      setCurrentCustomerCode(demoCustomerCode);
      setShowOverlay(true); // Reset overlay for demo video
    } else {
      setHasError(true);
    }
  };

  return (
    <div className={`rh-video-explainer py-12 sm:py-16 ${background}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="lg:col-span-6 lg:order-2">
            <h2
              className={`${titleColor} !pb-0 mb-2`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <p
              className={`font-selawik text-lg sm:text-xl leading-normal sm:leading-normal max-w-prose text-balance ${subtitleColorClass} mb-8`}
            >
              {subtitle}
            </p>
            <div className="mt-2">
              <a
                href="/about/about-us"
                className={`${ctaClassName} inline-flex items-center gap-2`}
              >
                Learn more about us
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    d="M13.5 5.5L20 12l-6.5 6.5m-9-6.5H20"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 lg:order-1">
            <div className="relative bg-primary rounded-2xl overflow-hidden group">
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                {!hasError && !showOverlay ? (
                  <div className="absolute inset-0">
                    <Stream
                      src={currentVideoId}
                      customerCode={currentCustomerCode}
                      controls
                      responsive
                      preload="metadata"
                      onError={handleVideoError}
                      streamRef={streamRef as any}
                      width="100%"
                      height="100%"
                    />
                  </div>
                ) : hasError ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-primary text-white">
                    <div className="text-center p-8">
                      <h3 className="!pb-0 mb-2 text-xl">Video temporarily unavailable</h3>
                      <p className="!text-base text-default-300 mb-4">
                        {currentVideoId === demoVideoId
                          ? "We're having trouble loading both the main video and demo fallback. Please check your internet connection or try again later."
                          : "We're working to resolve this issue. Please try again later."}
                      </p>
                      <div className="space-y-3">
                        {fallbackVideoUrl && (
                          <a
                            href={fallbackVideoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block rounded-full bg-white px-6 py-2 font-semibold text-primary transition-colors hover:bg-default-200"
                          >
                            Watch on external player
                          </a>
                        )}
                        <div>
                          <button
                            onClick={() => window.location.reload()}
                            className="inline-block rounded-full border border-white/40 px-6 py-2 font-semibold text-white transition-colors hover:bg-white/10"
                          >
                            Refresh page
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Custom poster overlay that hides when clicked */}
                {showOverlay && (
                  <>
                    {/* Poster Image (use next/image so it can be optimized/resized) */}
                    <div className="absolute inset-0 z-10">
                      <Image
                        src={posterUrl}
                        alt="Video preview"
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>

                    {/* Dark Overlay */}
                    <div className="absolute inset-0 bg-black/10 z-20" />

                    {/* Play Button */}
                    <button
                      onClick={handlePlayClick}
                      className="absolute inset-0 flex items-center justify-center group hover:bg-black/20 transition-all duration-300 motion-reduce:transition-none z-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
                      aria-label="Play video"
                    >
                      <div className="flex items-center justify-center w-20 h-20 bg-white rounded-full group-hover:scale-110 transition-all duration-300 motion-reduce:transition-none shadow-2xl">
                        <svg
                          className="w-8 h-8 text-primary ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoExplainer;
